import type {
  BankruptcyType,
  Customer,
  DebtAgencyCounsellorType,
} from './types';

/**
 * Staff flags as read back from GET /customers/{customerId} (admin API only).
 *
 * Six flags are plain booleans. Bankrupt and Debt Agency & Counsellor are one
 * flag each on the backend but carry a `type` discriminator, and staff see the
 * variant ("Part 9", not "Bankrupt") — so the read side exposes the type and
 * the chip label comes from it. Absent (exclude_none) means not set; the FE
 * never sees `null` here.
 *
 * `directDebitBlocked` is the odd one out: the system sets it when the bank
 * refuses a direct debit (PowerPay FATAL, BE PR #1088) and only replacing the
 * bank account clears it. There is no apply/remove route, so — like
 * `duplicate` — it is a chip only, never an Add Flag or Remove Flag option.
 *
 * `hardship` and `monitor` mean *currently in effect*: both can carry an expiry
 * and read `false` once it has passed even though staff never removed them.
 */

/** The flag-bearing slice of the profile. */
export type CustomerFlagState = Pick<
  Customer,
  | 'monitor'
  | 'hardship'
  | 'cardPaymentsRestricted'
  | 'fraud'
  | 'loa'
  | 'directDebitBlocked'
  | 'bankruptcyType'
  | 'debtAgencyCounsellorType'
  | 'duplicate'
  | 'duplicateOf'
>;

/** One rendered chip in the client header. `key` is stable for styling/tests. */
export type CustomerFlagChipKey =
  | 'monitor'
  | 'hardship'
  | 'cardPaymentsRestricted'
  | 'fraud'
  | 'loa'
  | 'directDebitBlocked'
  | 'bankruptcy'
  | 'debtAgencyCounsellor'
  | 'duplicate';

export interface CustomerFlagChip {
  key: CustomerFlagChipKey;
  label: string;
  /**
   * Duplicate only: the `userId` of the profile this one duplicates (same
   * name + DOB + postcode), so the pill can link to it. Absent when the
   * backend flagged the duplicate without naming the original.
   */
  duplicateOf?: string;
}

/** Wire values of `bankruptcyType` → chip label. `OTHER` is plain bankruptcy. */
export const BANKRUPTCY_TYPE_LABELS: Record<BankruptcyType, string> = {
  BANK9: 'Part 9',
  BANK10: 'Part 10',
  OTHER: 'Bankrupt',
};

/** Wire values of `debtAgencyCounsellorType` → chip label. */
export const DEBT_AGENCY_COUNSELLOR_TYPE_LABELS: Record<
  DebtAgencyCounsellorType,
  string
> = {
  DEBT_AGENCY: 'Debt Agency',
  COUNSELLOR: 'Financial Counsellor',
};

const BOOLEAN_FLAG_CHIPS: ReadonlyArray<{
  key: Extract<
    CustomerFlagChipKey,
    | 'monitor'
    | 'hardship'
    | 'cardPaymentsRestricted'
    | 'fraud'
    | 'loa'
    | 'directDebitBlocked'
  >;
  label: string;
}> = [
  { key: 'monitor', label: 'Monitor' },
  { key: 'hardship', label: 'Financial Hardship' },
  { key: 'cardPaymentsRestricted', label: 'Card Payments Restricted' },
  { key: 'fraud', label: 'Fraud' },
  { key: 'loa', label: 'Letter of Authority' },
  // Staff flags first, then the system detection.
  { key: 'directDebitBlocked', label: 'Direct Debit Blocked' },
];

/**
 * Every flag chip the client header should render, in display order.
 *
 * Flags stack — a customer can legitimately be Monitor + Financial Hardship +
 * Card Payments Restricted at once — so this is a set, not a single status.
 * The two typed flags are independent of each other (a customer flagged
 * bankrupt after entering a debt arrangement carries both), so both chips can
 * show together. An unrecognised type value still renders, using the raw wire
 * value as its label, so a new backend enum member degrades visibly rather
 * than dropping the flag.
 */
export function getCustomerFlagChips(
  customer: CustomerFlagState | undefined
): CustomerFlagChip[] {
  if (!customer) return [];

  const chips: CustomerFlagChip[] = BOOLEAN_FLAG_CHIPS.filter(
    ({ key }) => customer[key] === true
  ).map(({ key, label }) => ({ key, label }));

  if (customer.bankruptcyType) {
    chips.push({
      key: 'bankruptcy',
      label:
        BANKRUPTCY_TYPE_LABELS[customer.bankruptcyType] ??
        customer.bankruptcyType,
    });
  }

  if (customer.debtAgencyCounsellorType) {
    chips.push({
      key: 'debtAgencyCounsellor',
      label:
        DEBT_AGENCY_COUNSELLOR_TYPE_LABELS[customer.debtAgencyCounsellorType] ??
        customer.debtAgencyCounsellorType,
    });
  }

  if (customer.duplicate === true) {
    chips.push({
      key: 'duplicate',
      label: 'Duplicate',
      ...(customer.duplicateOf ? { duplicateOf: customer.duplicateOf } : {}),
    });
  }

  return chips;
}

/* ------------------------------------------------------------------------ */
/* Write side — POST /customers/{customerId}/{slug}, 202 Accepted, async      */
/* ------------------------------------------------------------------------ */

/**
 * The seven staff flags and their apply/remove endpoint slugs. Card Payments
 * Restricted is the only slug that is not the flag name (the path says
 * `restriction`, the profile field says `cardPaymentsRestricted`). Part 9 /
 * Part 10 / Bankrupt are ONE flag (`bankrupt`) told apart by `type`; likewise
 * Debt Agency / Financial Counsellor. Letter of Authority (`loa`, BE PR #1100)
 * is shaped like Card Payments Restricted: plain boolean, no expiry, reason
 * only — and it is visual-only on the backend.
 */
export const CUSTOMER_FLAG_SLUGS = {
  hardship: { apply: 'apply-hardship', remove: 'remove-hardship' },
  monitor: { apply: 'apply-monitor', remove: 'remove-monitor' },
  cardPaymentsRestricted: {
    apply: 'apply-card-payments-restriction',
    remove: 'remove-card-payments-restriction',
  },
  bankrupt: { apply: 'apply-bankrupt', remove: 'remove-bankrupt' },
  fraud: { apply: 'apply-fraud', remove: 'remove-fraud' },
  loa: { apply: 'apply-loa', remove: 'remove-loa' },
  debtAgencyCounsellor: {
    apply: 'apply-debt-agency-counsellor',
    remove: 'remove-debt-agency-counsellor',
  },
} as const;

export type CustomerFlag = keyof typeof CUSTOMER_FLAG_SLUGS;
export type CustomerFlagAction = 'apply' | 'remove';
export type CustomerFlagType = BankruptcyType | DebtAgencyCounsellorType;

/**
 * Body shared by all fourteen routes. `reason` is mandatory everywhere (400
 * otherwise, and it is read back by staff on the audit log). `expiry` is
 * hardship/monitor only and must be strictly in the future; omit it for "until
 * staff remove the flag". `type` is required on apply-bankrupt and
 * apply-debt-agency-counsellor and is ignored on every remove.
 */
export interface CustomerFlagPayload {
  reason: string;
  expiry?: string | null;
  type?: CustomerFlagType;
}

/**
 * One row of the Add Flag picker: the ten chip labels staff know, each
 * resolving to the backend flag (and `type`, where the flag carries one).
 */
export interface CustomerFlagOption {
  /** Stable picker value, e.g. `bankrupt:BANK9`. */
  value: string;
  label: string;
  flag: CustomerFlag;
  type?: CustomerFlagType;
  /** Hardship and Monitor may carry a cooling-off expiry. */
  supportsExpiry: boolean;
}

export const CUSTOMER_FLAG_OPTIONS: readonly CustomerFlagOption[] = [
  {
    value: 'hardship',
    label: 'Financial Hardship',
    flag: 'hardship',
    supportsExpiry: true,
  },
  { value: 'monitor', label: 'Monitor', flag: 'monitor', supportsExpiry: true },
  {
    value: 'cardPaymentsRestricted',
    label: 'Card Payments Restricted',
    flag: 'cardPaymentsRestricted',
    supportsExpiry: false,
  },
  { value: 'fraud', label: 'Fraud', flag: 'fraud', supportsExpiry: false },
  {
    value: 'bankrupt:BANK9',
    label: BANKRUPTCY_TYPE_LABELS.BANK9,
    flag: 'bankrupt',
    type: 'BANK9',
    supportsExpiry: false,
  },
  {
    value: 'bankrupt:BANK10',
    label: BANKRUPTCY_TYPE_LABELS.BANK10,
    flag: 'bankrupt',
    type: 'BANK10',
    supportsExpiry: false,
  },
  {
    value: 'bankrupt:OTHER',
    label: BANKRUPTCY_TYPE_LABELS.OTHER,
    flag: 'bankrupt',
    type: 'OTHER',
    supportsExpiry: false,
  },
  {
    value: 'debtAgencyCounsellor:DEBT_AGENCY',
    label: DEBT_AGENCY_COUNSELLOR_TYPE_LABELS.DEBT_AGENCY,
    flag: 'debtAgencyCounsellor',
    type: 'DEBT_AGENCY',
    supportsExpiry: false,
  },
  {
    value: 'debtAgencyCounsellor:COUNSELLOR',
    label: DEBT_AGENCY_COUNSELLOR_TYPE_LABELS.COUNSELLOR,
    flag: 'debtAgencyCounsellor',
    type: 'COUNSELLOR',
    supportsExpiry: false,
  },
  {
    value: 'loa',
    label: 'Letter of Authority',
    flag: 'loa',
    supportsExpiry: false,
  },
];

/** True when the backend would refuse apply with CUSTOMER_FLAG_ALREADY_SET. */
function isFlagActive(state: CustomerFlagState, flag: CustomerFlag): boolean {
  switch (flag) {
    case 'bankrupt':
      return Boolean(state.bankruptcyType);
    case 'debtAgencyCounsellor':
      return Boolean(state.debtAgencyCounsellorType);
    default:
      return state[flag] === true;
  }
}

/**
 * Add Flag options for this customer. A flag that is already active is hidden
 * — the typed flags gate on presence, not value, because a customer on Part 9
 * must not be offered Part 10 (apply-bankrupt 409s whichever type you send).
 * Debt Agency & Counsellor is also hidden while the customer is bankrupt
 * (409 BANKRUPT_FLAG_TAKES_PRECEDENCE); bankruptcy stays available regardless.
 * An expired hardship/monitor flag reads `false`, so it IS offered again:
 * re-applying restarts it with the new expiry. Letter of Authority is never
 * hidden by another flag — it coexists with a debt arrangement and with
 * bankruptcy alike (the backend keeps it out of the precedence chain).
 */
export function getAddableFlagOptions(
  state: CustomerFlagState | undefined
): CustomerFlagOption[] {
  if (!state) return [];
  return CUSTOMER_FLAG_OPTIONS.filter(option => {
    if (isFlagActive(state, option.flag)) return false;
    if (option.flag === 'debtAgencyCounsellor' && state.bankruptcyType) {
      return false;
    }
    return true;
  });
}

export interface RemovableFlag {
  flag: CustomerFlag;
  /** The chip label staff see ("Part 9", not "bankrupt"). */
  label: string;
}

const CHIP_KEY_TO_FLAG: Partial<Record<CustomerFlagChipKey, CustomerFlag>> = {
  monitor: 'monitor',
  hardship: 'hardship',
  cardPaymentsRestricted: 'cardPaymentsRestricted',
  fraud: 'fraud',
  loa: 'loa',
  bankruptcy: 'bankrupt',
  debtAgencyCounsellor: 'debtAgencyCounsellor',
};

/**
 * Remove Flag options: the active chips mapped to their backend flag. Duplicate
 * is a detection, not a staff flag, so it is not removable here; the same goes
 * for Direct Debit Blocked, which the system sets and only a bank-account
 * replacement clears (no remove route exists). Both drop out because they have
 * no CHIP_KEY_TO_FLAG entry. An expired hardship/monitor flag is not listed
 * either — it reads `false` — although the backend would still accept
 * `remove-*` for it.
 */
export function getRemovableFlags(
  state: CustomerFlagState | undefined
): RemovableFlag[] {
  return getCustomerFlagChips(state).flatMap(chip => {
    const flag = CHIP_KEY_TO_FLAG[chip.key];
    return flag ? [{ flag, label: chip.label }] : [];
  });
}
